import { HandbookSourcesEditor } from "../handbook_sources_editor"
import { HandbookMediaEditor } from "../handbook_media_editor"
import { cloneMediaUsages, removeMediaUsage } from "../handbook_media_usages"
import { Controller } from "@hotwired/stimulus"
import { blockLabels, newBlock, readingMetrics, effectiveMetadata, childLabels, newChild, prepareEditor, serializableDocument, strongSelection, linkSelection, slides, flattenBlocks, newSlide, duplicateSlide, duplicateBlock, slideContainingBlock } from "../handbook_document"

export default class extends Controller {
  static targets = ["document", "blocks", "addSlide", "metrics", "hint", "status", "defaults", "mediaUsages", "mediaConfig", "hero", "picker", "sources", "sourceRows", "addSource"]
  static values = { dirty: Boolean }

  connect() {
    this.submitting = false
    this.fieldSequence = 0
    try {
      this.content = JSON.parse(this.documentTarget.value)
      if (this.content?.schema_version !== 1 || !Array.isArray(this.content.slides) || this.content.slides.some(slide => !slide || typeof slide.id !== "string" || !Array.isArray(slide.blocks)) || flattenBlocks(this.content).some(block => !block || !Object.hasOwn(blockLabels, block.type))) throw new Error("format")
      this.media = new HandbookMediaEditor(this, JSON.parse(this.mediaUsagesTarget.value), JSON.parse(this.mediaConfigTarget.dataset.assets), this.mediaConfigTarget.dataset.pickerUrl)
      prepareEditor(this.content)
      this.render()
      this.updateMetrics()
    } catch {
      this.content = null
      this.statusTarget.textContent = "Nie można otworzyć przesłanego dokumentu. Zachowano dane poniżej; skopiuj je przed odświeżeniem."
      const recovery = this.node("textarea", { readOnly: true, className: "form-control", value: this.documentTarget.value })
      recovery.setAttribute("aria-label", "Zachowany dokument")
      this.blocksTarget.replaceChildren(recovery)
      const mediaRecovery = this.node("textarea", { readOnly: true, className: "form-control", value: this.mediaUsagesTarget.value })
      mediaRecovery.setAttribute("aria-label", "Zachowane użycia mediów")
      this.heroTarget.replaceChildren(mediaRecovery)
    }
    this.sources = new HandbookSourcesEditor(this)
    this.initial = this.snapshot()
    if (this.dirtyValue) this.statusTarget.focus()
  }

  snapshot() { return JSON.stringify([...new FormData(this.element)].filter(([key]) => key !== "authenticity_token")) }
  disconnect() { this.media?.dispose() }
  addSource() { this.sources.add() }
  serialize() {
    this.sources?.serialize()
    if (!this.content) return
    this.documentTarget.value = JSON.stringify(serializableDocument(this.content))
    this.mediaUsagesTarget.value = JSON.stringify(this.media.rows)
  }
  changed() {
    this.serialize()
    this.dirtyValue = this.snapshot() !== this.initial || this.element.dataset.failedSave === "true"
    if (this.content) this.updateMetrics()
  }
  submit() { this.serialize(); this.submitting = true }
  submitted(event) { if (!event.detail.success) this.submitting = false }
  beforeUnload(event) {
    if (!this.dirtyValue || this.submitting) return
    event.preventDefault()
    event.returnValue = "Masz niezapisane zmiany."
  }
  beforeVisit(event) {
    if (this.dirtyValue && !this.submitting && !window.confirm("Masz niezapisane zmiany. Opuścić edytor?")) event.preventDefault()
  }
  otherSubmit(event) {
    if (event.target !== this.element && this.dirtyValue && !window.confirm("Masz niezapisane zmiany. Opuścić edytor?")) event.preventDefault()
  }
  clear() { this.media?.dispose(); this.dirtyValue = false; this.element.replaceChildren() }

  node(tag, properties = {}) { return Object.assign(document.createElement(tag), properties) }
  button(label, action) {
    const button = this.node("button", { type: "button", className: "btn btn-muted", textContent: label })
    button.addEventListener("click", action)
    return button
  }
  field(card, label, value, update, tag = "textarea") {
    const id = `handbook-field-${++this.fieldSequence}`
    const input = this.node(tag, { id, className: "form-control", value })
    input.setAttribute("aria-describedby", "handbook-errors")
    const change = () => { update(input.value); this.changed() }
    input.addEventListener("input", change)
    input.addEventListener("change", change)
    const wrapper = this.node("div")
    wrapper.append(this.node("label", { htmlFor: id, textContent: label }), input)
    card.append(wrapper)
    return input
  }
  addSlide() {
    if (!this.content) return
    const slide = newSlide()
    this.content.slides.push(slide)
    this.renderSlideFocus(slide.id)
    this.changed()
    this.statusTarget.textContent = `Dodano slajd ${this.content.slides.length}.`
  }
  addBlock(slide, type) {
    const block = newBlock(type)
    slide.blocks.push(block)
    this.render(block.id)
    this.changed()
    this.statusTarget.textContent = `Dodano blok: ${blockLabels[block.type]}.`
  }
  renderSlideFocus(id, action) {
    this.render()
    const card = [...this.blocksTarget.querySelectorAll("[data-slide-id]")].find(card => card.dataset.slideId === id)
    const field = action ? [...(card?.querySelectorAll("button") || [])].find(button => button.dataset.slideAction === action) : card?.querySelector("textarea:not(:disabled), select") || card?.querySelector("button:not([aria-disabled=true])")
    ;(field || this.addSlideTarget).focus()
  }
  renderSlide(slide, index) {
    const card = this.node("section", { className: "border-2 rounded p-4 space-y-4" })
    card.dataset.slideId = slide.id
    card.setAttribute("aria-label", `Slajd ${index + 1}`)
    card.append(this.node("h3", { className: "text-xl font-semibold", textContent: `Slajd ${index + 1}` }))
    const controls = this.node("div", { className: "flex flex-wrap gap-2" })
    for (const [label, offset] of [["Slajd wyżej", -1], ["Slajd niżej", 1]]) {
      const button = this.button(label, () => {
        const target = index + offset
        if (target < 0 || target >= this.content.slides.length) return
        this.content.slides.splice(index, 1); this.content.slides.splice(target, 0, slide)
        this.renderSlideFocus(slide.id, label); this.changed()
        this.statusTarget.textContent = `Przeniesiono slajd na pozycję ${target + 1}.`
      })
      button.dataset.slideAction = label
      button.setAttribute("aria-disabled", String(index + offset < 0 || index + offset >= this.content.slides.length))
      controls.append(button)
    }
    controls.append(this.button("Duplikuj slajd", () => {
      const copy = duplicateSlide(slide)
      cloneMediaUsages(this.media.rows, slide.blocks, copy.blocks)
      this.content.slides.splice(index + 1, 0, copy)
      this.renderSlideFocus(copy.id); this.changed()
      this.statusTarget.textContent = "Skopiowano slajd jako nowy slajd."
    }), this.button("Usuń slajd", () => {
      if (!window.confirm(`Usunąć slajd ${index + 1} wraz ze wszystkimi blokami?`)) return
      slide.blocks.forEach(block => removeMediaUsage(this.media.rows, block.id))
      this.content.slides.splice(index, 1)
      this.renderSlideFocus(this.content.slides[Math.min(index, this.content.slides.length - 1)]?.id)
      this.changed(); this.statusTarget.textContent = "Usunięto slajd."
    }))
    card.append(controls)
    slide.blocks.forEach((block, position) => card.append(this.renderBlock(block, position, slide.blocks)))
    const kind = this.field(card, "Typ nowego bloku", "paragraph", () => {}, "select")
    for (const [value, label] of Object.entries(blockLabels)) kind.append(this.node("option", { value, textContent: label }))
    kind.value = "paragraph"
    card.append(this.button("Dodaj blok", () => this.addBlock(slide, kind.value)))
    return card
  }
  inlineField(card, label, value, update) {
    let segments = typeof value === "string" ? [{ text: value }] : value.segments || [{ text: value.text }]
    if (!segments.length) segments = [{ text: "" }]
    const container = this.node("div", { className: "space-y-3" })
    const result = this.node("p", { className: "whitespace-pre-wrap border-l-2 pl-3" })
    result.setAttribute("aria-label", `${label} — wynik formatowania`)
    // Unsaved URLs are displayed as text, never as navigable unvalidated links.
    const showResult = () => result.replaceChildren(...segments.map(segment => this.node(segment.strong ? "strong" : "span", {
      textContent: segment.text, className: Object.hasOwn(segment, "href") ? "underline" : "",
      title: Object.hasOwn(segment, "href") ? `Link: ${segment.href}` : ""
    })))
    const draw = (focusIndex) => {
      container.replaceChildren()
      segments.forEach((segment, index) => {
        const input = this.field(container, segments.length === 1 ? label : `${label} — fragment ${index + 1}`, segment.text, text => {
          segment.text = text; update({ segments }); showResult()
        })
        const controls = this.node("div", { className: "flex flex-wrap gap-2" })
        const bold = this.button("Pogrub zaznaczenie", () => {
          if (Object.hasOwn(segment, "href")) {
            this.statusTarget.textContent = "Link nie może być pogrubiony. Najpierw usuń link."
            input.focus(); return
          }
          const start = input.selectionStart, end = input.selectionEnd
          if (start === end) {
            this.statusTarget.textContent = "Najpierw zaznacz fragment tekstu w polu."
            input.focus(); return
          }
          segments = strongSelection(segments, index, start, end)
          update({ segments }); this.changed(); draw(index + (start > 0 ? 1 : 0))
          this.statusTarget.textContent = "Pogrubiono zaznaczenie. Fragmenty można dalej edytować osobno."
        })
        // Keep the textarea selection on pointer activation; keyboard activation uses the retained selection.
        bold.addEventListener("mousedown", event => event.preventDefault())
        controls.append(bold)
        if (segment.strong) controls.append(this.button("Usuń pogrubienie", () => {
          delete segment.strong; update({ segments }); this.changed(); draw(index)
        }))
        if (Object.hasOwn(segment, "href")) {
          this.field(container, `Adres linku — fragment ${index + 1}`, segment.href, href => {
            segment.href = href; update({ segments }); showResult()
          }, "input")
          controls.append(this.button("Usuń link", () => {
            delete segment.href; update({ segments }); this.changed(); draw(index)
            this.statusTarget.textContent = "Usunięto link. Tekst pozostał bez zmian."
          }))
        } else {
          const link = this.button("Dodaj link", () => {
            if (segment.strong) {
              this.statusTarget.textContent = "Link nie może być pogrubiony. Najpierw usuń pogrubienie."
              input.focus(); return
            }
            const start = input.selectionStart, end = input.selectionEnd
            if (start === end) {
              this.statusTarget.textContent = "Najpierw zaznacz fragment tekstu w polu."
              input.focus(); return
            }
            link.disabled = true
            const editor = this.node("div", { className: "space-y-2" })
            const url = this.field(editor, "Adres nowego linku", "", () => {}, "input")
            url.placeholder = "https://example.org, /strona lub #kotwica"
            editor.append(this.button("Zastosuj link", () => {
              if (!url.value.trim()) {
                this.statusTarget.textContent = "Wpisz adres linku."; url.focus(); return
              }
              segments = linkSelection(segments, index, start, end, url.value)
              update({ segments }); this.changed(); draw(index + (start > 0 ? 1 : 0))
              this.statusTarget.textContent = "Dodano link. Adres zostanie sprawdzony przy zapisie."
            }), this.button("Anuluj link", () => {
              editor.remove(); link.disabled = false; input.focus()
            }))
            controls.after(editor)
            url.focus()
          })
          link.addEventListener("mousedown", event => event.preventDefault())
          controls.append(link)
        }
        container.append(controls)
      })
      container.append(this.node("p", { className: "text-sm", textContent: "Zaznacz tekst w polu, aby dodać pogrubienie lub link. Wynik:" }), result)
      showResult()
      if (focusIndex !== undefined) container.querySelectorAll("textarea")[focusIndex]?.focus()
    }
    card.append(container)
    draw()
  }
  setInline(block, value) { delete block.text; block.segments = value.segments }

  render(focusId, focusAction) {
    this.media.renderHero()
    this.blocksTarget.replaceChildren(...slides(this.content).map((slide, index) => this.renderSlide(slide, index)))
    if (focusId) {
      const card = this.card(focusId)
      const field = focusAction ? [...card.querySelectorAll("button")].find(button => button.dataset.move === focusAction && button.closest("[data-block-id]") === card) : card.querySelector("textarea, select, button")
      field?.focus()
    }
  }
  renderBlock(block, index, siblings, parent) {
    const child = !!parent
    const card = this.node(child ? "div" : "fieldset", { className: "border rounded p-4 space-y-3 min-w-0" })
    card.dataset.blockId = block.id
    const label = `${index + 1}. ${(child ? childLabels : blockLabels)[block.type]}`
    if (child) { card.setAttribute("role", "group"); card.setAttribute("aria-label", label) }
    card.append(this.node(child ? "p" : "legend", { className: "font-semibold px-2", textContent: label }))
    if (block.type === "heading") {
      const level = this.field(card, "Poziom nagłówka", block.level, value => { block.level = Number(value) }, "select")
      for (const number of [2, 3]) level.append(this.node("option", { value: number, textContent: `H${number}` }))
      level.value = block.level
      card.append(this.node("p", { className: "text-sm break-all", textContent: block.anchor ? `Stała kotwica: #${block.anchor}` : "Kotwica powstanie z tekstu przy pierwszym zapisie." }))
    }
    if (block.type === "list") {
      const ordered = this.field(card, "Rodzaj listy", "", value => { block.ordered = value === "true" }, "select")
      ordered.append(this.node("option", { value: "false", textContent: "Punktowana" }), this.node("option", { value: "true", textContent: "Numerowana" }))
      ordered.value = String(block.ordered)
      block.items.forEach((item, itemIndex) => {
        this.inlineField(card, `Punkt ${itemIndex + 1}`, item, value => { block.items[itemIndex] = value })
        card.append(this.button(`Usuń punkt ${itemIndex + 1}`, () => {
          block.items.splice(itemIndex, 1); this.render(block.id); this.changed()
        }))
      })
      card.append(this.button("Dodaj punkt", () => {
        block.items.push(""); this.render(); this.changed()
        const fields = this.card(block.id).querySelectorAll("textarea")
        fields[fields.length - 1].focus()
      }))
    } else if (["remember", "warning"].includes(block.type)) {
      if (!Array.isArray(block.children)) throw new Error("Niepoprawny callout")
      block.children.forEach((element, childIndex) => {
        if (!Object.hasOwn(childLabels, element.type)) throw new Error("Niepoprawny element calloutu")
        card.append(this.renderBlock(element, childIndex, block.children, block))
      })
      const add = this.node("div", { className: "flex flex-wrap gap-2" })
      for (const [type, name] of Object.entries(childLabels)) add.append(this.button(`+ ${name}`, () => {
        const element = newChild(type); block.children.push(element)
        this.render(element.id); this.changed()
        this.statusTarget.textContent = `Dodano element calloutu: ${name}.`
      }))
      card.append(add)
    } else if (block.type === "image") {
      this.media.render(block.id, card)
    } else if (["heading", "subheading"].includes(block.type)) {
      this.field(card, block.type === "subheading" ? "Śródtytuł" : "Tekst", block.text, value => { block.text = value })
    } else this.inlineField(card, "Tekst", block, value => this.setInline(block, value))

    const controls = this.node("div", { className: "flex flex-wrap gap-2" })
    for (const [name, offset] of [["Wyżej", -1], ["Niżej", 1]]) {
      const button = this.button(name, () => {
        const target = index + offset
        if (target < 0 || target >= siblings.length) return
        siblings.splice(index, 1); siblings.splice(target, 0, block)
        this.render(block.id, name); this.changed()
        this.statusTarget.textContent = `Przesunięto ${child ? "element" : "blok"} na pozycję ${target + 1}.`
      })
      button.dataset.move = name
      button.setAttribute("aria-label", `${name} — ${child ? "element" : "blok"} ${index + 1}`)
      button.setAttribute("aria-disabled", String(index + offset < 0 || index + offset >= siblings.length))
      controls.append(button)
    }
    controls.append(this.button(child ? "Duplikuj element" : "Duplikuj blok", () => {
      const copy = duplicateBlock(block)
      cloneMediaUsages(this.media.rows, [block], [copy])
      siblings.splice(index + 1, 0, copy)
      this.render(copy.id); this.changed()
      this.statusTarget.textContent = "Skopiowano blok z nową tożsamością."
    }))
    if (!child) {
      const slide = slideContainingBlock(this.content, block.id)
      const slideIndex = this.content.slides.indexOf(slide)
      for (const [label, offset] of [["Do poprzedniego slajdu", -1], ["Do następnego slajdu", 1]]) {
        const target = this.content.slides[slideIndex + offset]
        const button = this.button(label, () => {
          if (!target) return
          siblings.splice(index, 1)
          if (offset < 0) target.blocks.push(block)
          else target.blocks.unshift(block)
          this.render(block.id); this.changed()
          this.statusTarget.textContent = `Przeniesiono blok do slajdu ${slideIndex + offset + 1}.`
        })
        button.setAttribute("aria-disabled", String(!target))
        controls.append(button)
      }
    }
    controls.append(this.button(child ? "Usuń element" : "Usuń blok", () => {
      removeMediaUsage(this.media.rows, block.id)
      siblings.splice(index, 1)
      this.render(siblings[Math.min(index, siblings.length - 1)]?.id || parent?.id)
      if (!siblings.length && !parent) {
        const slide = slideContainingBlock(this.content, block.id)
        // Removed blocks have no owner anymore; find the same blocks array.
        this.renderSlideFocus(slide?.id || this.content.slides.find(slide => slide.blocks === siblings)?.id)
      }
      this.changed(); this.statusTarget.textContent = child ? "Usunięto element calloutu." : "Usunięto blok."
    }))
    card.append(controls)
    return card
  }
  card(id) { return [...this.blocksTarget.querySelectorAll("[data-block-id]")].find(card => card.dataset.blockId === id) }
  updateMetrics() {
    const value = name => this.element.elements.namedItem(`handbook_section_revision[${name}]`).value
    const metrics = readingMetrics({ title: value("title"), h1: value("h1"), lead: value("lead"), content: this.content })
    this.metricsTarget.textContent = `${metrics.words} słów · około ${metrics.minutes} min czytania`
    this.hintTarget.hidden = metrics.words <= 1200
    const defaults = effectiveMetadata({ title: value("title"), h1: value("h1"), lead: value("lead"), html_title: value("html_title"), meta_description: value("meta_description"), content: this.content })
    this.defaultsTarget.textContent = `H1: ${defaults.h1} · HTML title: ${defaults.title} · Opis: ${defaults.description}`
  }
}
